import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import Modal from "@/components/Modal";
import MediaInput from "@/components/MediaInput";
import MediaViewer from "@/components/MediaViewer";
import HeirAssignmentPicker from "@/components/HeirAssignmentPicker";
import ExampleHint from "@/components/ExampleHint";
import {
  Plus,
  MessageSquareHeart,
  Pencil,
  Trash2,
  Mail,
  CalendarClock,
  Mic,
  Video,
  FileText,
  Paperclip,
} from "lucide-react";

const EMPTY = {
  recipient_name: "",
  recipient_email: "",
  title: "",
  body: "",
  media_type: "text",
  media_data: "",
  media_filename: "",
  media_size: 0,
  deliver_at: "",
  deliver_on_pol_trigger: false,
  message_category: "date",
  assigned_heirs: [],
};

const MEDIA_ICON = {
  text: FileText,
  audio: Mic,
  video: Video,
  image: Paperclip,
  file: Paperclip,
};

export default function Messages() {
  const { t } = useI18n();
  const CATEGORY_META = {
    date: { emoji: "📅", label: t("msg_cat_date_label"), hint: t("msg_cat_date_hint") },
    death: { emoji: "🕊️", label: t("msg_cat_death_label"), hint: t("msg_cat_death_hint") },
    historia: { emoji: "📖", label: t("msg_cat_historia_label"), hint: t("msg_cat_historia_hint") },
  };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const r = await api.get("/messages");
    setItems(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (m) => {
    setEditingId(m.message_id);
    setForm({
      recipient_name: m.recipient_name,
      recipient_email: m.recipient_email,
      title: m.title,
      body: m.body || "",
      media_type: m.media_type || "text",
      media_data: m.media_data || "",
      media_filename: m.media_filename || "",
      media_size: m.media_size || 0,
      deliver_at: m.deliver_at ? new Date(m.deliver_at).toISOString().split("T")[0] : "",
      deliver_on_pol_trigger: !!m.deliver_on_pol_trigger,
      message_category: m.message_category || "date",
      assigned_heirs: m.assigned_heirs || [],
    });
    setViewing(null);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (form.message_category === "date") {
      if (!form.deliver_at) {
        toast.error(t("msg_date_required"));
        return;
      }
      payload.deliver_at = new Date(form.deliver_at).toISOString();
    } else {
      // historia / death: backend handles deliver_at=null
      payload.deliver_at = null;
      payload.deliver_on_pol_trigger = true;
    }
    try {
      if (editingId) {
        const r = await api.put(`/messages/${editingId}`, payload);
        setItems((arr) => arr.map((x) => (x.message_id === editingId ? r.data : x)));
      } else {
        const r = await api.post("/messages", payload);
        setItems((arr) => [...arr, r.data]);
      }
      toast.success(t("saved"));
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/messages/${id}`);
    toast.success(t("deleted"));
    setItems((arr) => arr.filter((x) => x.message_id !== id));
    if (viewing?.message_id === id) setViewing(null);
  };

  return (
    <div data-testid="messages-page">
      <PageHeader
        title={t("msg_title")}
        subtitle={t("msg_subtitle")}
        action={
          <button className="btn-gold" onClick={openCreate} data-testid="msg-add-btn">
            <Plus className="w-4 h-4" /> {t("msg_add")}
          </button>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={MessageSquareHeart}>{t("msg_empty")}</EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((m) => {
            const Icon = MEDIA_ICON[m.media_type] || MessageSquareHeart;
            return (
              <button
                key={m.message_id}
                onClick={() => setViewing(m)}
                className="glass-card rounded-md p-5 text-left hover:border-gold transition"
                data-testid={`msg-${m.message_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-md bg-gold/10 border border-gold-subtle">
                    <Icon className="w-5 h-5 text-gold" strokeWidth={1.4} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-gold-light truncate flex items-center gap-2">
                      <span>{CATEGORY_META[m.message_category || "date"].emoji}</span>
                      {m.title}
                    </div>
                    <div className="text-sm text-muted truncate">
                      {m.recipient_name} ·{" "}
                      {m.deliver_at
                        ? new Date(m.deliver_at).toLocaleDateString()
                        : CATEGORY_META[m.message_category || "date"].label}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <ExampleHint slug="messages" testid="messages-hint" />

      {/* VIEW */}
      <Modal
        open={!!viewing && !open}
        onClose={() => setViewing(null)}
        title={viewing?.title || ""}
        testid="msg-view-modal"
      >
        {viewing && (
          <div className="space-y-5">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted">
                <Mail className="w-4 h-4 text-gold" /> {viewing.recipient_name} ·{" "}
                <span className="text-gold-light">{viewing.recipient_email}</span>
              </div>
              <div className="flex items-center gap-2 text-muted">
                <CalendarClock className="w-4 h-4 text-gold" />
                {viewing.deliver_at
                  ? new Date(viewing.deliver_at).toLocaleDateString()
                  : `${CATEGORY_META[viewing.message_category || "date"].emoji} ${CATEGORY_META[viewing.message_category || "date"].label}`}
              </div>
            </div>
            <div className="divider-gold" />
            {viewing.body && (
              <div data-testid="msg-view-body">
                <div className="font-label text-faded mb-3">{t("msg_your_message")}</div>
                <div className="text-gold-light whitespace-pre-wrap leading-relaxed font-body">
                  {viewing.body}
                </div>
              </div>
            )}
            {viewing.media_data && (
              <div data-testid="msg-view-media">
                {viewing.body && <div className="font-label text-faded mb-3">{t("msg_attachment_label")}</div>}
                <MediaViewer media={viewing} />
              </div>
            )}
            {!viewing.body && !viewing.media_data && (
              <div className="text-faded italic text-sm">{t("msg_no_body")}</div>
            )}
            <div className="flex gap-3 justify-end pt-3 border-t border-gold-subtle">
              <button
                className="btn-ghost"
                onClick={() => remove(viewing.message_id)}
                data-testid="msg-view-delete"
              >
                <Trash2 className="w-4 h-4" /> {t("delete")}
              </button>
              <button className="btn-gold" onClick={() => openEdit(viewing)} data-testid="msg-view-edit">
                <Pencil className="w-4 h-4" /> {t("edit")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* CREATE/EDIT */}
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditingId(null);
        }}
        title={editingId ? t("edit") : t("msg_add")}
        testid="msg-modal"
      >
        <form onSubmit={save} className="space-y-4">
          {/* Category selector */}
          <div>
            <label className="font-label text-faded block mb-2">{t("msg_type_label")}</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {Object.entries(CATEGORY_META).map(([k, meta]) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => setForm({ ...form, message_category: k })}
                  className={`text-left p-3 rounded-md border transition ${
                    form.message_category === k
                      ? "bg-gold/10 border-gold text-gold-light"
                      : "border-gold-subtle text-muted hover:border-gold"
                  }`}
                  data-testid={`msg-cat-${k}`}
                >
                  <div className="text-base">
                    {meta.emoji} {meta.label}
                  </div>
                  <div className="text-[11px] text-faded mt-1 leading-tight">{meta.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-label text-faded block mb-2">{t("msg_recipient")}</label>
            <input
              className="input-field"
              required
              value={form.recipient_name}
              onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
              data-testid="msg-recipient-name"
            />
          </div>
          <div>
            <label className="font-label text-faded block mb-2">{t("msg_recipient_email")}</label>
            <input
              type="email"
              className="input-field"
              required
              value={form.recipient_email}
              onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
              data-testid="msg-recipient-email"
            />
          </div>
          <div>
            <label className="font-label text-faded block mb-2">{t("msg_subject")}</label>
            <input
              className="input-field"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="msg-subject"
            />
          </div>

          <MediaInput
            textLabel={t("msg_body")}
            textValue={form.body}
            textRows={6}
            onTextChange={(v) => setForm({ ...form, body: v })}
            media={form}
            onMediaChange={(m) => setForm({ ...form, ...m })}
          />

          {form.message_category === "date" ? (
            <div>
              <label className="font-label text-faded block mb-2">{t("msg_date")}</label>
              <div className="relative">
                <CalendarClock className="w-5 h-5 text-gold absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="date"
                  className="input-field pl-12 pr-4 py-4 text-base cursor-pointer w-full"
                  required
                  min={new Date().toISOString().split("T")[0]}
                  value={form.deliver_at}
                  onChange={(e) => setForm({ ...form, deliver_at: e.target.value })}
                  data-testid="msg-date"
                  onClick={(e) => {
                    if (typeof e.currentTarget.showPicker === "function") {
                      try { e.currentTarget.showPicker(); } catch (_) {}
                    }
                  }}
                />
              </div>
              {form.deliver_at && (
                <p className="text-sm text-gold-light mt-2" data-testid="msg-date-pretty">
                  {new Date(form.deliver_at + "T00:00:00").toLocaleDateString(undefined, {
                    weekday: "long", year: "numeric", month: "long", day: "numeric",
                  })}
                </p>
              )}
            </div>
          ) : (
            <div className="bg-gold/[0.05] border border-gold-subtle rounded-md p-3 text-sm text-gold-light flex gap-2">
              <span className="text-base">{CATEGORY_META[form.message_category].emoji}</span>
              <div>
                <div>{CATEGORY_META[form.message_category].label}</div>
                <div className="text-xs text-muted mt-0.5">{CATEGORY_META[form.message_category].hint}</div>
              </div>
            </div>
          )}

          <div className="divider-gold" />
          <HeirAssignmentPicker
            value={form.assigned_heirs}
            onChange={(arr) => setForm({ ...form, assigned_heirs: arr })}
            label={t("msg_assign_heirs_label")}
          />

          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
              {t("cancel")}
            </button>
            <button type="submit" className="btn-gold" data-testid="msg-save">
              {t("save")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
